import React from 'react'
import { BgVertical, BgHorizontal } from "@/components/background";
import { PublicFooter as Footer, PublicGetStarted as GetStarted, PublicHeader as Header, PublicMiniHeader as MiniHeader, PublicMobileMenu as MenuHorizontal } from '@/components/public/PublicChrome';
import HeadMeta from '@/components/HeadMeta';
import Logo from '@/components/brand/Logohorizontal';
import Link from 'next/link';
import Cookies from '@/components/tools/Cookies';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { IconGoal, IconLock, IconStackedcard, IconStartext } from '../icons';
import { useTranslation } from 'react-i18next';
import WebMcpTools from './WebMcpTools';
import { LANDING_LOCALES, landingUrl } from './landingLocales';
import { changeAppLanguage, rememberAppLanguage } from '@/i18n';
import CefrChart from './CefrChart';

const DeferredLandingContent = ({
  loader,
  render,
  minHeight = 120,
}: {
  loader: () => Promise<{ default: React.ComponentType<any> }>;
  render: (Component: React.ComponentType<any>) => React.ReactNode;
  minHeight?: number;
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [Component, setComponent] = React.useState<React.ComponentType<any> | null>(null);

  React.useEffect(() => {
    if (!ref.current || Component) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          void loader().then(({ default: LoadedComponent }) => {
            setComponent(() => LoadedComponent);
          });
        }
      },
      { rootMargin: '400px 0px' },
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [Component, loader]);

  return (
    <div ref={ref} style={{ minHeight }}>
      {Component ? render(Component) : null}
    </div>
  );
};

export type LandingStats = { courses: number; lessons: number; languages: number };

export const HomePage = ({ className, stats, locale = 'en' }: { className: string; stats?: LandingStats | null; locale?: string }) => {
  const { t } = useTranslation();
  const current = LANDING_LOCALES.find(l => l.code === locale) || LANDING_LOCALES[0];
  const alternates = [
    ...LANDING_LOCALES.map(l => ({ hreflang: l.hreflang, href: landingUrl(l.code) })),
    { hreflang: 'x-default', href: landingUrl('en') },
  ];
  const router = useRouter();

  // Structured data for SEO

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://mynawoo.com/#organization",
        "name": "Mynawoo",
        "url": "https://mynawoo.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://brand.mynawoo.com/icon.png"
        },
        "sameAs": [
          // لینک شبکه‌های واقعی رو اینجا بذار (اگر نداری، همین key رو حذف کن)
          // "https://instagram.com/mynawoo",
          // "https://twitter.com/mynawoo"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://mynawoo.com/#website",
        "url": "https://mynawoo.com",
        "name": "Mynawoo",
        "publisher": { "@id": "https://mynawoo.com/#organization" },
        "hasPart": { "@id": "https://mynawoo.com/courses/#collection" },
        "inLanguage": current.hreflang
      },
      {
        "@type": "CollectionPage",
        "@id": "https://mynawoo.com/courses/#collection",
        "url": "https://mynawoo.com/courses",
        "name": "Mynawoo Language Courses",
        "description": "Browse Mynawoo language courses with CEFR A1–C2 outlines and free lesson previews.",
        "isPartOf": { "@id": "https://mynawoo.com/#website" },
        "about": { "@id": "https://mynawoo.com/#organization" }
      },
      {
        "@type": "WebApplication",
        "@id": "https://mynawoo.com/#app",
        "name": "Mynawoo",
        "url": "https://mynawoo.com",
        "applicationCategory": "EducationalApplication",
        "operatingSystem": "Web",
        "description":
          "AI-powered language learning platform that helps you reach B2 in about 9 months through your mother tongue, with grammar, reading, listening, writing, and speaking practice.",
        "publisher": { "@id": "https://mynawoo.com/#organization" },
        "offers": {
          "@type": "Offer",
          "category": "Education",
          "name": "Mynawoo Language Learning",
          "url": "https://mynawoo.com/#unique-features",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
          // اگر قیمت داری اضافه کن:
          // "price": "9.99"
        }
      },
      {
        "@type": "OfferCatalog",
        "@id": "https://mynawoo.com/#catalog",
        "name": "Mynawoo Learning Programs",
        "url": "https://mynawoo.com/courses",
        "itemListElement": [
          {
            "@type": "Offer",
            "name": "Grammar Lessons",
            "category": "Education",
            "url": "https://mynawoo.com/#learning-methods",
            "itemOffered": {
              "@type": "Course",
              "name": "Grammar Lessons",
              "description": "Step-by-step grammar lessons with guided explanations and teacher-style voice.",
              "provider": { "@id": "https://mynawoo.com/#organization" }
            }
          },
          {
            "@type": "Offer",
            "name": "Reading Practice",
            "category": "Education",
            "url": "https://mynawoo.com/#learning-methods",
            "itemOffered": {
              "@type": "Course",
              "name": "Reading Practice",
              "description": "Reading comprehension with audio, vocabulary support, and quizzes.",
              "provider": { "@id": "https://mynawoo.com/#organization" }
            }
          },
          {
            "@type": "Offer",
            "name": "Listening Practice",
            "category": "Education",
            "url": "https://mynawoo.com/#learning-methods",
            "itemOffered": {
              "@type": "Course",
              "name": "Listening Practice",
              "description": "Listening exercises with instant feedback, quizzes, and corrections.",
              "provider": { "@id": "https://mynawoo.com/#organization" }
            }
          },
          {
            "@type": "Offer",
            "name": "Writing Practice",
            "category": "Education",
            "url": "https://mynawoo.com/#learning-methods",
            "itemOffered": {
              "@type": "Course",
              "name": "Writing Practice",
              "description": "Writing prompts with topic guidance and AI corrections.",
              "provider": { "@id": "https://mynawoo.com/#organization" }
            }
          },
          {
            "@type": "Offer",
            "name": "Speaking Practice",
            "category": "Education",
            "url": "https://mynawoo.com/#learning-methods",
            "itemOffered": {
              "@type": "Course",
              "name": "Speaking Practice",
              "description": "Speaking practice with guidance, corrections, and confidence-building drills.",
              "provider": { "@id": "https://mynawoo.com/#organization" }
            }
          }
        ]
      }
    ]
  };

  return (
    <>
      <WebMcpTools />
      {/* SEO and Meta Data Section */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <HeadMeta
        title={locale === 'en' ? "Mynawoo | Learn Languages with AI" : `Mynawoo | ${t('landing.learn_any_language_through')} ${t('landing.your_own_mother_tongue')}`}
        seoConfig={{
          description: locale === 'en'
            ? "Learn any language with AI—through your mother tongue. Mynawoo combines grammar-first CEFR lessons (A1–C2) with reading, listening, writing, and speaking practice, smart flashcards and AI exams."
            : t('landing.hero_description'),
          imageUrl: "https://brand.mynawoo.com/icon.png",
          siteUrl: landingUrl(current.code),
          locale: current.hreflang,
          alternates,
          keywords:
            "Mynawoo, learn languages with AI, AI language learning, language learning platform, grammar-first, CEFR, A1 to C2, speaking practice, listening practice, reading practice, writing practice, smart flashcards, spaced repetition, adaptive learning, language course, online language learning"
        }}
      />

      {/* Navigation Section */}
      <section className="navigation-section hidden lg:block">
          <BgHorizontal disablePatttern classNameBase='!fixed top-0 left-0' />
          <Header />
          <div className='fixed bottom-10 right-10 z-[25] space-y-2 w-1/3'>
            <Cookies />
            <GetStarted />
          </div>
      </section>
      <section className="navigation-section lg:hidden">
          <BgVertical disablePatttern classNameBase='!fixed top-0 left-0' />
          <div className=' flex items-center justify-between p-4'>
            <Link href="/" aria-label="Home">
              <Logo className='w-32 md:w-full' />
            </Link>
            <MiniHeader />
          </div>
          <div className=' fixed bottom-0 w-full z-[25] space-y-2'>
            <Cookies />
            <GetStarted />
            <MenuHorizontal isHeader />
          </div>
      </section>

      {/* Main Content Section: hook → problem → solution → skills → features → levels → proof → CTA */}
      <main dir={current.dir || 'ltr'} lang={current.code} className={`${current.dir === 'rtl' ? 'font-mikhak ' : ''}mx-auto max-w-lg space-y-12 px-4 pb-40 pt-2 lg:max-w-6xl lg:space-y-16 lg:px-8 lg:pb-24 lg:pt-12`}>
        {/* Hero */}
        <section className="hero-section">
          <header className="flex flex-col items-center gap-6 lg:flex-row-reverse lg:gap-14">
            <div className="space-y-5 text-center lg:w-1/2 lg:text-start">
              <p className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-bold text-primary-shadow lg:text-sm">
                <span className="h-2 w-2 animate-pulse rounded-full bg-[#58CC02]" aria-hidden />
                {t('landing.learn_any_language_through')}
              </p>
              <h1 className="text-4xl font-black leading-[1.05] text-white lg:text-6xl">
                <span className="bg-gradient-to-r from-[#9A6AF0] via-[#C9A7FF] to-[#F99A3A] bg-clip-text text-transparent">
                  {t('landing.your_own_mother_tongue')}
                </span>
              </h1>
              <p className="text-base leading-relaxed text-white/70 lg:max-w-xl lg:text-lg">
                {t('landing.hero_description')}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row lg:justify-start">
                <button type="button" onClick={() => router.push('/classroom')} aria-label="Start learning" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3.5 text-base font-extrabold uppercase tracking-wide text-white shadow-[0_5px_0_#4c3a9e] transition hover:brightness-110 active:translate-y-1 active:shadow-none sm:flex-1">
                  {t('common.get_started')}
                </button>
                <Link href="/courses" className="inline-flex items-center justify-center rounded-2xl border-2 border-white/15 bg-white/5 px-6 py-3 text-base font-extrabold uppercase tracking-wide text-white shadow-[0_5px_0_rgba(255,255,255,0.08)] transition hover:bg-white/10 active:translate-y-1 active:shadow-none sm:flex-1">
                  {t('courses.title')}
                </Link>
              </div>
              <ul className="flex flex-wrap justify-center gap-2 lg:justify-start" aria-label={t('landing.language_learning_methods')}>
                {[
                  ['landing.grammar', '#F99A3A'], ['landing.reading', '#58CC02'], ['landing.listening', '#1CB0F6'],
                  ['landing.writing', '#CE82FF'], ['landing.speaking', '#FF4B4B'],
                ].map(([key, color]) => (
                  <li key={key} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-bold text-white/80">
                    <span className="me-1.5 inline-block h-2 w-2 rounded-full" style={{ background: color }} aria-hidden />
                    {t(key)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative lg:w-1/2">
              <div className="absolute inset-x-6 bottom-6 top-16 rounded-full bg-primary/30 blur-3xl" aria-hidden />
              <Image
                src="/imgs/test/mynaiq-lcp.webp"
                width={300}
                height={418}
                alt="Mynawoo AI language learning platform mascot character"
                priority
                fetchPriority="high"
                decoding="async"
                quality={70}
                sizes="(min-width: 1024px) 384px, 300px"
                className="relative mx-auto h-[418px] w-[300px] lg:h-[534px] lg:w-96"
              />
            </div>
          </header>
          <dl className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:mt-10">
            {[
              stats?.courses ? [stats.courses.toLocaleString(), t('landing.stat_courses', { defaultValue: 'Courses' }), '#9A6AF0'] : null,
              stats?.lessons ? [`${stats.lessons.toLocaleString()}+`, t('landing.stat_lessons', { defaultValue: 'Lessons' }), '#58CC02'] : null,
              stats?.languages ? [stats.languages.toLocaleString(), t('landing.stat_languages', { defaultValue: 'Languages' }), '#1CB0F6'] : null,
              ['A1 → C2', t('landing.stat_levels', { defaultValue: 'CEFR levels' }), '#F99A3A'],
            ].filter(Boolean).map(item => {
              const [value, label, color] = item as string[];
              return (
                <div key={label} className="rounded-2xl border-2 border-white/10 bg-white/[0.04] px-4 py-3 text-center">
                  <dt className="order-2 text-xs font-bold uppercase tracking-wide text-white/50">{label}</dt>
                  <dd className="text-2xl font-black lg:text-3xl" style={{ color }} dir="ltr">{value}</dd>
                </div>
              );
            })}
          </dl>
        </section>

        {/* Problem */}
        <section className="problem-section" aria-labelledby="problem-heading">
          <div className="grid items-center gap-8 rounded-[2rem] border border-white/10 bg-white/[0.03] p-6 lg:grid-cols-2 lg:p-12">
            <div className="space-y-5">
              <span className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#FF4B4B]">01</span>
              <h2 id="problem-heading" className="text-3xl font-black text-white lg:text-5xl">{t('landing.problem')}</h2>
              {[
                [t('landing.other_apps'), t('landing.other_apps_description')],
                [t('landing.youtube_lessons'), t('landing.youtube_lessons_description')],
              ].map(([title, body]) => (
                <div key={title} className="flex gap-3 rounded-2xl bg-[#FF4B4B]/[0.08] p-4">
                  <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#FF4B4B] text-sm font-black text-white shadow-[0_3px_0_#D33131]" aria-hidden>✕</span>
                  <p className="text-sm leading-relaxed text-white/75 lg:text-base">
                    <strong className="text-white">{title}</strong> {body}
                  </p>
                </div>
              ))}
            </div>
            <Image
              src="/imgs/test/mynasurprise.webp"
              width={300}
              height={300}
              loading="lazy"
              alt="Mynawoo character showing surprise about language learning problems"
              sizes="(min-width: 1024px) 320px, 280px"
              className="mx-auto h-auto w-3/4 lg:w-80"
            />
          </div>
        </section>

        {/* Solution + skills */}
        <section className="solution-section space-y-10" aria-labelledby="solution-heading">
          <div className="space-y-3 text-center">
            <span className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#58CC02]">02</span>
            <h2 id="solution-heading" className="text-3xl font-black text-white lg:text-5xl">{t('landing.solution')}</h2>
            <p className="mx-auto max-w-xl text-base text-white/65 lg:text-xl">{t('landing.how_mynawoo_makes_real')}</p>
          </div>
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_1.4fr]">
            <Image
              src="/imgs/test/greenboardmyna.webp"
              width={500}
              height={500}
              loading="lazy"
              alt="Mynawoo character teaching on a green board showing language learning solution"
              sizes="(min-width: 1024px) 384px, 320px"
              className="mx-auto h-auto w-4/5 lg:w-96"
            />
            <div className="learning-methods-section grid gap-3 sm:grid-cols-2" aria-labelledby="learning-methods">
              <h3 id="learning-methods" className="sr-only">{t('landing.language_learning_methods')}</h3>
              {[
                { color: '#F99A3A', shade: '#C96E14', img: '/imgs/character/Notebooks.webp', title: <>{t('landing.step_by_step_grammar')} <b style={{ color: '#F99A3A' }}>{t('landing.grammar')}</b> {t('landing.grammar_lessons')}</>, body: t('landing.grammar_description') },
                { color: '#58CC02', shade: '#46A302', img: '/imgs/character/Books.webp', title: <><b style={{ color: '#58CC02' }}>{t('landing.reading')}</b> {t('landing.reading_description')}</>, body: t('landing.reading_subdescription') },
                { color: '#1CB0F6', shade: '#1899D6', img: '/imgs/character/Listenings.webp', title: <><b style={{ color: '#1CB0F6' }}>{t('landing.listening')}</b> {t('landing.listening_description')}</>, body: t('landing.listening_subdescription') },
                { color: '#CE82FF', shade: '#A568CC', img: '/imgs/character/Writings.webp', title: <><b style={{ color: '#CE82FF' }}>{t('landing.writing')}</b> {t('landing.writing_description')}</>, body: t('landing.writing_subdescription') },
                { color: '#FF4B4B', shade: '#D33131', img: '/imgs/character/Speakings.webp', soon: true, title: <>{t('landing.enter_a')} <b style={{ color: '#FF4B4B' }}>{t('landing.speaking')}</b> {t('landing.environment')}</>, body: t('landing.speaking_description') },
              ].map((skill, i) => (
                <article
                  key={i}
                  className={`flex gap-3 rounded-2xl border-2 border-white/10 bg-white/[0.04] p-4 ${i === 4 ? 'sm:col-span-2' : ''}`}
                  style={{ boxShadow: `0 4px 0 ${skill.shade}55` }}
                >
                  <span className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl" style={{ background: `${skill.color}22` }}>
                    <Image src={skill.img} width={44} height={44} loading="lazy" alt="" aria-hidden className="h-11 w-11 object-contain" />
                  </span>
                  <div className="min-w-0">
                    <h4 className="text-base font-extrabold leading-snug text-white">{skill.title}</h4>
                    <p className="mt-1 text-sm text-white/60">{skill.body}</p>
                    {skill.soon && (
                      <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold text-white" aria-label="Coming soon">
                        <IconLock className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('landing.soon')}
                      </span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Unique features */}
        <section className="unique-features-section space-y-8" aria-labelledby="unique-features">
          <div className="space-y-3 text-center">
            <span className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#1CB0F6]">03</span>
            <h2 id="unique-features" className="text-3xl font-black text-white lg:text-5xl">{t('landing.unique_features')}</h2>
          </div>
          <div className="features-grid-section grid gap-4 lg:grid-cols-3">
            {[
              { id: 'personal-themes', icon: <IconStartext />, color: '#FFC800', title: <>{t('landing.personal')} {t('landing.themes')}</>, body: t('landing.personal_themes_description') },
              { id: 'smart-flashcards', icon: <IconStackedcard />, color: '#9A6AF0', title: <>{t('landing.smart')} {t('landing.flashcards')}</>, body: t('landing.smart_flashcards_description') },
              { id: 'leitner-system', icon: <IconGoal />, color: '#F99A3A', title: <>{t('landing.leitner')} {t('landing.system')}</>, body: t('landing.leitner_system_description') },
            ].map(f => (
              <section key={f.id} aria-labelledby={f.id} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
                <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: f.color, boxShadow: `0 4px 0 ${f.color}99` }} aria-hidden>
                  {f.icon}
                </span>
                <h3 id={f.id} className="text-lg font-extrabold uppercase tracking-wide text-white">{f.title}</h3>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-white/65 lg:text-base">{f.body}</p>
              </section>
            ))}
          </div>
        </section>

        {/* CEFR levels */}
        <section className="grid items-center gap-10 lg:grid-cols-2" aria-labelledby="cefr-progression">
          <div className="space-y-6">
            <span className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#CE82FF]">04</span>
            <h2 id="cefr-progression" className="text-3xl font-black text-white lg:text-4xl">{t('landing.cefr_level_progression')}</h2>
            <ol className="relative space-y-3 border-s-2 border-dashed border-white/15 ps-6" role="list">
              {[
                ['A1', '#F99A3A', t('landing.a1_description')],
                ['A2', '#FFC800', t('landing.a2_description')],
                ['B1', '#9A6AF0', t('landing.b1_description')],
                ['B2', '#58CC02', t('landing.b2_description')],
                ['C1', '#1CB0F6', t('landing.c1_description', { defaultValue: 'Fluent, flexible language for complex topics' })],
                ['C2', '#FF4B4B', t('landing.c2_description', { defaultValue: 'Near-native precision and nuance' })],
              ].map(([level, color, text]) => (
                <li key={level} className="relative flex items-center gap-3 rounded-2xl bg-white/[0.04] p-3">
                  <span className="absolute -start-[37px] h-4 w-4 rounded-full ring-4 ring-black" style={{ background: color }} aria-hidden />
                  <strong className="flex h-11 w-11 flex-none items-center justify-center rounded-xl text-sm font-black text-[#111318]" style={{ background: color, boxShadow: `0 3px 0 ${color}99` }}>{level}</strong>
                  <p className="text-sm text-white/75 lg:text-base">{text}</p>
                </li>
              ))}
            </ol>
          </div>
          <CefrChart />
        </section>

        {/* Comparison */}
        <section className="grid items-center gap-8 rounded-[2rem] border border-white/10 bg-gradient-to-br from-primary/15 to-transparent p-6 lg:grid-cols-2 lg:p-12" aria-labelledby="comparison-heading">
          <h2 id="comparison-heading" className="text-center text-2xl font-black leading-snug text-white lg:text-start lg:text-4xl">
            {t('landing.comparison_with_other_apps')}
          </h2>
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-black/30">
            <div className="grid grid-cols-[1.3fr_1fr_1fr] text-xs font-extrabold uppercase tracking-wide lg:text-sm">
              <div className="p-3 text-white/50 lg:p-4">{t('landing.cmp_feature', { defaultValue: 'Feature' })}</div>
              <div className="bg-primary p-3 text-center text-white lg:p-4">Mynawoo</div>
              <div className="p-3 text-center text-white/50 lg:p-4">{t('landing.cmp_others', { defaultValue: 'Other apps' })}</div>
            </div>
            {[
              ['cmp_grammar', 'In-depth grammar aligned with CEFR', 'cmp_grammar_us', 'Fully structured', 'cmp_grammar_them', 'Limited or superficial'],
              ['cmp_srs', 'Smart flashcards with Leitner review', 'cmp_srs_us', 'Accurate & personal', 'cmp_srs_them', 'Little or no real SRS'],
              ['cmp_exams', 'Mock exams & structured practice', 'cmp_exams_us', 'Custom tests for every skill', 'cmp_exams_them', 'Short game-like quizzes'],
              ['cmp_prep', 'International exam preparation', 'cmp_prep_us', 'Serious & professional', 'cmp_prep_them', 'Mostly gamified'],
            ].map(([k, d, ku, du, kt, dt], i) => (
              <div key={k} className={`grid grid-cols-[1.3fr_1fr_1fr] items-stretch text-xs lg:text-sm ${i % 2 ? 'bg-white/[0.02]' : ''}`}>
                <div className="border-t border-white/[0.06] p-3 font-bold text-white lg:p-4">{t(`landing.${k}`, { defaultValue: d })}</div>
                <div className="flex items-start gap-1.5 border-t border-primary/30 bg-primary/15 p-3 text-white lg:p-4">
                  <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-[#58CC02] text-[10px] font-black text-white" aria-hidden>✓</span>
                  {t(`landing.${ku}`, { defaultValue: du })}
                </div>
                <div className="flex items-start gap-1.5 border-t border-white/[0.06] p-3 text-white/55 lg:p-4">
                  <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-[#FF4B4B]/80 text-[10px] font-black text-white" aria-hidden>✕</span>
                  {t(`landing.${kt}`, { defaultValue: dt })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Public user MCP connector — separate from admin MCP */}
        <section
          className="lg:max-w-5xl lg:mx-auto"
          aria-label={t('landing.mcp_title')}
        >
          <DeferredLandingContent
            minHeight={180}
            loader={() => import('@/components/tools/McpConnectGuide')}
            render={(Guide) => <Guide />}
          />
        </section>


        {/* section for magazine */}
        <section
          className="lg:max-w-6xl lg:mx-auto"
          aria-labelledby="magazine-section"
        >
          <h2 id="magazine-section" className="sr-only">{t('landing.magazine_section')}</h2>
          <DeferredLandingContent
            minHeight={180}
            loader={() => import('../classroom/Mag')}
            render={(Mag) => <Mag limit={5} />}
          />
          <div className='text-center mt-2'>
            <Link href="/mag" className='font-bold lg:text-lg' aria-label="See more magazines">{t('landing.see_more_magazines')}</Link>
          </div>
        </section>

        {/* Conclusion + final CTA */}
        <section aria-labelledby="conclusion" className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-primary via-[#6a4fd6] to-[#3b2c86] p-8 text-center shadow-[0_8px_0_#2e2270] lg:p-14">
          <span className="pointer-events-none absolute -end-16 -top-16 h-56 w-56 rounded-full bg-white/10" aria-hidden />
          <span className="pointer-events-none absolute -bottom-20 -start-10 h-56 w-56 rounded-full bg-white/[0.06]" aria-hidden />
          <p
            aria-hidden="true"
            className="relative bg-gradient-to-r from-[#B79CFF] via-[#E9C7F2] to-[#F9A66B] bg-clip-text text-4xl font-black tracking-tight text-transparent drop-shadow-[0_2px_0_rgba(0,0,0,0.15)] lg:text-6xl"
          >
            {t('landing.conclusion_label', { defaultValue: 'Conclusion:' })}
          </p>
          <h2 id="conclusion" className="relative mt-4 text-3xl font-black text-white lg:text-5xl">
            {t('landing.real_learning_lasting_forever')} <span aria-hidden="true">✨</span>
          </h2>
          <p className="relative mx-auto mt-3 max-w-2xl text-base text-white/85 lg:text-xl">{t('landing.mynawoo_teaches_tests_adapts')}</p>
          <p className="relative mt-1 font-bold text-white/85 lg:text-lg">{t('landing.learn_what_you_love')} <span aria-hidden="true">💜</span></p>
          <button
            type="button"
            onClick={() => router.push('/classroom')}
            aria-label="Start learning the right way"
            className="relative mt-8 inline-flex items-center justify-center rounded-2xl bg-white px-8 py-4 text-base font-extrabold uppercase tracking-wide text-primary shadow-[0_5px_0_rgba(0,0,0,0.25)] transition hover:brightness-95 active:translate-y-1 active:shadow-none"
          >
            {t('landing.start_learning_right_way')}
          </button>
        </section>
        {/* Crawlable links to every language version */}
        <nav aria-label="Languages" className="flex flex-wrap justify-center gap-2 pt-4">
          {LANDING_LOCALES.map(l => (
            <a
              key={l.code}
              href={l.code === 'en' ? '/' : `/${l.code}`}
              hrefLang={l.hreflang}
              lang={l.code}
              aria-current={l.code === current.code ? 'page' : undefined}
              onClick={(e) => {
                // Save the choice first so the remembered language doesn't redirect back.
                e.preventDefault();
                rememberAppLanguage(l.code, router.locales);
                void changeAppLanguage(l.code);
                void router.push('/', '/', { locale: l.code });
              }}
              className={`rounded-full border px-3 py-1 text-xs font-bold transition ${l.code === current.code ? 'border-primary bg-primary/20 text-white' : 'border-white/10 text-white/55 hover:text-white'}`}
            >
              {l.name}
            </a>
          ))}
        </nav>
      </main>
      <Footer />
    </>
  )
};
